/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { ActionType, infigo } from '@dm/live-material-infigo';

import GrammarEN from '../grammar/en-US';
import GrammarZH_extra from '../grammar/extra-zh';
import { DEFAULT_URL, IgnoreCaseMatch, mapType, mapTypeChinese } from '../grammar/gammar';
import GrammarZH from '../grammar/zh-CN';
import { ErrorType } from './errorType';
import {
    type GrammarList,
    $$default,
    isBooleanGrammar,
    isDateGrammar,
    isEnumGrammar,
    isNumGrammar,
    isPerGrammar,
    isStringGrammar,
    TokenType,
} from './grammar.type';
import * as GrammarUtil from './grammar.util';

export class Translator {
    grammarList = {};
    language;

    constructor(language) {
        this.language = language;
        if (language === 'zh') {
            this.add(GrammarZH);
            this.add(GrammarZH_extra);
        } else {
            this.add(GrammarEN);
        }
    }

    // 添加语法
    add(grammar: GrammarList): void {
        const sourceType = Object.keys(grammar);
        for (const type of sourceType) {
            if (this.grammarList[type]) {
                // 如果已经存在该类型，则合并字段
                GrammarUtil.rebase(this.grammarList[type], grammar[type]);
                continue;
            }
            // 如果不存在，则添加该字段
            GrammarUtil.add(this.grammarList, type, grammar[type]);
        }
    }

    /**
     *  校验语法完整性,并生成提示信息
     *  {
     *     type:LUN,
     *     params:[
     *         {key:Capacity,Symbol:>,value:5}
     *     ]
     * } =>
     * {
     *     type:LUN,
     *     params:[
     *         ....
     *     ]
     * }
     * @param ast
     * @param i18n
     */
    validate(ast, i18n): any {
        ast.type = ast.type.toUpperCase();
        const sourceType = mapType(ast.type);
        if (!sourceType) {
            throw new Error('ast must has the right sourceType');
        }
        let [grammarList, innerType] = IgnoreCaseMatch(sourceType, this.grammarList);
        innerType = this.language === 'zh' ? mapTypeChinese(innerType) : innerType;
        if (!grammarList) {
            throw new Error(`There are no grammar for ${sourceType}`);
        }
        // 尚未输入参数时候，提示可输入Key值。
        if (!ast.params.length) {
            GrammarUtil.tip(grammarList.sort((d, i) => i.index || 0 - d.index || 0).map((i) => {
                if (typeof i.key === 'string') {
                    return { normal: innerType + ' ' + i.key + ' ' };
                } else {
                    return { normal: innerType + ' ' + i.key[0] + ' ' };
                }
            }));
            return [];
        }
        // 暂不支持多个条件混合
        if (ast.params.length > 1) {
            GrammarUtil.error({
                type: ErrorType.NO_MATCH_KEY,
                value: ast.params[1].key,
            });
        }
        return ast.params.map((param) => {
            const { key, symbol, value } = param;
            const match = GrammarUtil.findMatchGrammar(key, grammarList);
            if (!match.length) {
                GrammarUtil.error({ type: ErrorType.NO_MATCH_KEY, value: key });
            }
            const completeMatch = match.filter((i) => i.isCompleteMatch);
            if (completeMatch.length > 1) {
                throw new Error(`you maybe add same grammar key like ${completeMatch[0].matchKey}`);
            }
            /**
             * 如果不存在完全匹配项，应该提示Key值 或者 判定为输入不正确 不给提示
             */
            if (completeMatch.length === 0) {
                if (symbol || value) {
                    GrammarUtil.error({
                        type: ErrorType.ERROR_KEY_TYPE,
                        value: key,
                    });
                }
                GrammarUtil.generateTips(match, innerType);
                return null;
            }
            /**
             * 已知存在完全匹配Key的语法项目
             * 情况一：用户已输入value,symbol,根据语法项的类型进行提示和Value完整程度提示或者生成API
             * 情况二：用户只输入到symbol，尚未输入value，根据语法项的类型进行提示，枚举提示枚举值，
             * 情况三：用户仅输入了Key值，但是完全匹配的语法项具有默认symbol，直接提示值。
             * 情况四：用户仅仅输入Key值，但是完全匹配的语法项不具有默认Symbol，提示Symbol
             */
            const matchGrammar = completeMatch[0].matchItem;
            if (value) {
                if (matchGrammar.tip) {
                    infigo.store.dispatch({
                        type: ActionType.UpdateInputTip,
                        value: matchGrammar.tip,
                    });
                }
                if (symbol === $$default && !matchGrammar.$default) {
                    GrammarUtil.error({ type: ErrorType.INVALID_SYMBOL, value: value.value });
                }
                if (symbol !== $$default && !matchGrammar.allowSymbol.includes(symbol)) {
                    GrammarUtil.error({ type: ErrorType.INVALID_SYMBOL, value: symbol });
                }
                // 如果validator存在则对数据进行校验
                if (matchGrammar.validator) {
                    try {
                        const validateResult = matchGrammar.validator(value.value);
                        if (!validateResult) throw new Error();
                    } catch (e) {
                        GrammarUtil.error({ type: ErrorType.INVALID_VALUE, value: value.value, tip: e.message });
                    }
                }
                if (isStringGrammar(matchGrammar)) {
                    return {
                        field: matchGrammar.value,
                        matchMode: GrammarUtil.getSymbol(matchGrammar, symbol),
                        value: value.value,
                        api: matchGrammar.api,
                    };
                }
                // 数字类型需要校验值类型和validator
                if (isNumGrammar(matchGrammar)) {
                    if (value.type !== TokenType.Number) {
                        GrammarUtil.error({ type: ErrorType.TYPE_ERROR, value: value.value });
                    }
                    return GrammarUtil.numSymbolToGrammar(matchGrammar.value, symbol, value.value, matchGrammar.api);
                }
                // 对于枚举值需要检查输入值是否完整，如果不完整则提示补充对应枚举值，如果完整，转换为API参数
                // 如果模糊匹配到不到对应枚举值，则提示错误。
                if (isEnumGrammar(matchGrammar) || isBooleanGrammar(matchGrammar) || isPerGrammar(matchGrammar)) {
                    if (!matchGrammar.enumValue) {
                        throw new Error(`the ${matchGrammar.type} does not has enumValue`);
                    }
                    const matchEnum = GrammarUtil.findMatchEnum(value, matchGrammar.enumValue);
                    const completeMatchEnum = matchEnum.filter((i) => i.isCompleteMatch);
                    if (matchEnum.length === 0) {
                        GrammarUtil.error({ type: ErrorType.NE_ENUM_VALUE, value: value.value });
                    }
                    if (completeMatchEnum.length > 1) {
                        throw new Error(`grammar ${matchGrammar.type} should not has the repeat enumValue`);
                    }
                    if (completeMatchEnum.length === 0) {
                        if (matchEnum.length >= 1) {
                            GrammarUtil.generateEnumTips(matchEnum, value,
                                `${innerType} ${key}${symbol === $$default ? '' : ` ${symbol}`}`);
                        }
                        return null;
                    }
                    if (isPerGrammar(matchGrammar)) {
                        const PMS_LUN_STATUS = infigo.getData(Symbol.for('PMS_LUN_STATUS'));
                        if (PMS_LUN_STATUS.status !== true) {
                            GrammarUtil.error({
                                type: 'PMS_LUN_STATUS_NOT_VALID',
                                value: key,
                                tip: !PMS_LUN_STATUS.abnormal ? i18n.get('monitor.charts_term_noConfigPMS_tip1') :
                                    i18n.get('monitor.charts_term_noConfigPMS_tip2')
                                    + i18n.get('monitor.charts_term_noConfigPMS_tip3'),
                            });
                        }
                        return {
                            TIME_SEGMENT: 'one_day',
                            compute_mode: 'avg',
                            indicators: [matchGrammar.value],
                            limit: 15,
                            object_type: sourceType,
                            sort_indicator: matchGrammar.value,
                            sort_type: value.value.toLowerCase(),
                            api: matchGrammar.api,
                            field: matchGrammar.value,
                        };
                    }
                    const mapKey = completeMatchEnum[0][value.value];
                    return {
                        field: matchGrammar.value,
                        matchMode: GrammarUtil.getSymbol(matchGrammar, symbol),
                        value: completeMatchEnum[0][mapKey] || mapKey,
                        api: matchGrammar.api,
                    };
                }
                // 日期型枚举值需要校验值的格式，并且将对应日期转换为时间戳
                if (isDateGrammar(matchGrammar)) {
                    const timeStamp = infigo.global.convertToStamps(value.value + ' 00:00:00');
                    return GrammarUtil.numSymbolToGrammar(matchGrammar.value, symbol, timeStamp, matchGrammar.api, true);
                }
            }
            const matchItem: any = completeMatch[0].matchItem;
            const preTip = `${innerType} ${key}`;
            if (symbol || matchItem.$default) {
                const matchSymbols = matchGrammar.allowSymbol.filter((i) => i.indexOf(symbol) === 0);
                const completeMatchSymbols = matchGrammar.allowSymbol.filter((i) => i === symbol);
                if ((matchGrammar.$default || completeMatchSymbols.length === 1) && matchGrammar.tip) {
                    infigo.store.dispatch({ type: ActionType.UpdateInputTip, value: matchGrammar.tip });
                }
                if (symbol && symbol !== $$default && matchSymbols.length === 0) {
                    GrammarUtil.error({ type: ErrorType.INVALID_SYMBOL, value: symbol });
                }
                if (process.env.NODE_ENV === 'development') {
                    if (completeMatchSymbols.length > 1) {
                        throw new Error('you can not add the same symbol in grammar');
                    }
                }
                if (isEnumGrammar(matchItem) || isBooleanGrammar(matchItem) || isPerGrammar(matchItem)) {
                    if (completeMatchSymbols.length === 0 && symbol) {
                        GrammarUtil.tip(matchSymbols.map((item) => ({
                            normal: `${preTip} ${symbol}`,
                            emphasis: item.slice(symbol.length),
                        })));
                    } else {
                        infigo.store.dispatch({ type: ActionType.UpdateInputTip, value: matchGrammar.tip });
                        const grammar = this.language === 'zh' ? GrammarZH : GrammarEN;
                        const [matchGram] = IgnoreCaseMatch(sourceType, grammar);
                        const tips = Array.from((matchGram[matchGrammar.api] as Array<any>)
                            .find((i) => i.value === matchGrammar.value)
                            .enumValue.keys());
                        GrammarUtil.tip(tips.map((tip) => ({
                            normal: `${preTip}${symbol ? ` ${symbol} ` : ' '}`,
                            emphasis: tip,
                        })));
                    }
                }
                if (isNumGrammar(matchItem)) {
                    if (symbol && matchSymbols.length > 0 && completeMatchSymbols.length !== 1) {
                        GrammarUtil.tip(matchSymbols.map((item) => ({
                            normal: `${preTip} ${symbol}`,
                            emphasis: item.slice(symbol.length),
                        })));
                    }
                    if (completeMatchSymbols.length === 1) {
                        infigo.store.dispatch({ type: ActionType.UpdateInputTip, value: matchGrammar.tip });
                    }
                }
                return null;
            }
            GrammarUtil.tip(matchItem.allowSymbol.map((sym) => ({
                normal: `${preTip} `,
                emphasis: sym,
            })));
            return null;
        });
    }

    // 将语法转换为API
    transform(ast, userInput, i18n): any {
        const params = this.validate(ast, i18n).filter((i) => i);
        const sourceType = mapType(ast.type);
        if (!params || params.length === 0) {
            return {
                type: sourceType,
                url: DEFAULT_URL[sourceType],
                filters: [],
                renderType: 'normal',
                isDefault: true,
            };
        }
        const api = params[0].api;
        return {
            type: sourceType,
            url: api,
            filters: params,
            field: params[0].field,
        };
    }

    // 从抽象语法树中获取字符型tip
    generateTip(ast): any {
        ast.type = ast.type.toUpperCase();
        const sourceType = mapType(ast.type);
        if (!sourceType) {
            return '';
        }
        const [grammarList] = IgnoreCaseMatch(sourceType, this.grammarList);
        if (grammarList && ast.params.length === 1) {
            const { key, symbol, value } = ast.params[0];
            if (key && symbol && value) {
                const match = GrammarUtil.findMatchGrammar(key, grammarList);
                const completeMatch = match.filter((i) => i.isCompleteMatch);
                if (completeMatch) {
                    const matchGrammar = completeMatch[0].matchItem;
                    return matchGrammar.tip || '';
                }
            }
        }
        return '';
    }
}


